import { ChatBox, type ChatBoxProps } from '@mui/x-chat';
import type { ChatPartRendererMap, ChatPartRendererProps } from '@mui/x-chat/headless';
import { useRef } from 'react';

import { AgenticChatActivityGroup } from './Activity/Group';
import { AgenticChatWorkingIndicator } from './Activity/WorkingIndicator';
import { AgenticChatComposerInput } from './ComposerInput';
import { AgenticChatEmptyState } from './EmptyState';
import { AgenticChatScrollToBottomButton } from './ScrollToBottom/Button';
import { AgenticChatSendButton } from './SendButton';
import { AgenticChatStopOnEscape } from './StopOnEscape';
import { type AgenticChatController } from '../../../components/AgenticChat/context';
import { AgenticChatQuestionPart } from '../Question/Part';
import { AGENTIC_CHAT_QUESTION_PART_TYPE } from '../Question/utils';

interface AgenticChatConversationChatProps extends Pick<
  ChatBoxProps,
  'adapter' | 'initialMessages' | 'initialActiveConversationId' | 'onError' | 'onFinish'
> {
  agenticChatVisibility: AgenticChatController['agenticChatVisibility'];
}

// MUI republishes renderers through context when this map changes identity.
const agenticChatPartRenderers: ChatPartRendererMap = {
  tool: AgenticChatActivityGroup,
  'dynamic-tool': AgenticChatActivityGroup,
  reasoning: AgenticChatActivityGroup,
  [AGENTIC_CHAT_QUESTION_PART_TYPE]: (props: ChatPartRendererProps) => (
    <AgenticChatQuestionPart
      data={'data' in props.part ? props.part.data : undefined}
      message={props.message}
    />
  ),
};

export function AgenticChatConversationChat(props: AgenticChatConversationChatProps) {
  const { initialMessages = [], agenticChatVisibility, ...chatProps } = props;
  const chatRef = useRef<HTMLDivElement>(null);

  return (
    <ChatBox
      {...chatProps}
      ref={chatRef}
      partRenderers={agenticChatPartRenderers}
      initialMessages={initialMessages}
      variant="compact"
      layoutMode="standard"
      localeText={{
        composerInputAriaLabel: 'Message Flow AI',
        composerInputPlaceholder: 'Ask, or describe what to do…',
      }}
      features={{
        attachments: false,
        conversationHeader: false,
        conversationList: false,
        dateDivider: true,
        helperText: false,
      }}
      slots={{
        messageAvatar: null,
        messageAuthorName: null,
        messageMeta: null,
        messageInlineMeta: null,
        streamingIndicator: AgenticChatWorkingIndicator,
        scrollToBottom: AgenticChatScrollToBottomButton,
        emptyState: AgenticChatEmptyState,
        composerInput: AgenticChatComposerInput,
      }}
      slotProps={{
        messageList: {
          // Keep the bottom fade behind the composer and controls that open over messages.
          sx: { isolation: 'isolate' },
        },
        composerRoot: {
          variant: 'default',
        },
        composerInput: {
          autoFocus: initialMessages.length === 0,
          // MUI Chat alpha.16 fixes the height when maxRows is set; the theme bounds auto-sizing.
          maxRows: undefined,
          slotProps: {
            input: { rows: 3 },
          },
        },
        composerSendButton: {
          slots: {
            sendButton: AgenticChatSendButton,
          },
        },
      }}
      sx={{ flex: 1, minHeight: 0 }}
    >
      <AgenticChatStopOnEscape chatRef={chatRef} agenticChatVisibility={agenticChatVisibility} />
    </ChatBox>
  );
}
